import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { PortraitFrame } from '../components/PortraitFrame';
import { SectionNav } from '../components/SectionNav';
import { PROFILE } from '../data/profile';
const FACTS = [{
  label: 'Current Role',
  value: 'US Head of Finance, Bank of Ireland'
}, {
  label: 'Based In',
  value: 'New York City Metropolitan Area'
}, {
  label: 'Nationality & Origin',
  value: 'Dublin, Ireland — resident in the United States'
}, {
  label: 'Professional Standing',
  value: 'Active Certified Public Accountant (CPA), New York'
}];
const PRINCIPLES = [{
  title: 'Governance before growth',
  body: 'Sustainable performance in a regulated institution begins with control integrity, transparent reporting, and a board that can see the balance sheet clearly.'
}, {
  title: 'Capital is a strategic asset',
  body: 'Every regulatory constraint carries an optimization opportunity. Disciplined interpretation of the rules releases capital that funds the business.'
}, {
  title: 'Zero-failure remediation',
  body: 'Supervisory findings are addressed once, permanently, and at the root cause — with an evidence trail a regulator and an audit committee can both follow.'
}];
export function About() {
  return <>
      <PageHeader index="01" title="About Gerry Holmes" intro="A finance executive shaped inside globally systemic banks — and the leadership philosophy he brings into the boardroom." data-id="element-302" data-mp="1yjn4q3b94ynj" />

      <section aria-labelledby="about-bio" className="w-full bg-white" data-id="element-303" data-mp="28i2tw0qpxxue">
        <div className="mx-auto max-w-content px-6 py-24 sm:py-28 lg:px-8" data-id="element-304" data-mp="2m8y8zurt9b6f">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-20" data-id="element-305" data-mp="2mb2bc8a2a24l">
            <div className="lg:col-span-5" data-id="element-306" data-mp="2i7w879i8wr2c">
              <PortraitFrame tone="light" size="md" showCaption={false} data-id="element-307" data-mp="2w75e9gcqm4sl" />
              <div className="mt-12 border-t border-slateink-300/70 pt-8" data-id="element-308" data-mp="2w519yxaxf3rs">
                <h2 className="text-[11px] font-semibold uppercase tracking-widest text-navy-700" data-id="element-309" data-mp="2r9doarvnksr1">
                  Profile at a Glance
                </h2>
                <dl className="mt-6 space-y-5" data-id="element-310" data-mp="1svyig94nenyj">
                  {FACTS.map(fact => <div key={fact.label} data-id="element-311" data-mp="1xvkxmo41ee9z">
                      <dt className="text-[10px] uppercase tracking-widest text-slateink-500" data-id="element-312" data-mp="1xujahl9qy1bw">
                        {fact.label}
                      </dt>
                      <dd className="mt-1.5 text-[15px] leading-snug text-navy-900" data-id="element-313" data-mp="27le6vigpihb2">
                        {fact.value}
                      </dd>
                    </div>)}
                </dl>
              </div>
            </div>

            <div className="lg:col-span-7" data-id="element-314" data-mp="2gmc2bcd2xgi1">
              <h2 id="about-bio" className="font-serif text-3xl leading-tight text-navy-900 sm:text-[38px]" data-id="element-315" data-mp="2lkndprj0vz2d">
                Twenty years of finance leadership at the centre of global banking.
              </h2>

              <div className="mt-8 space-y-6 text-[16px] leading-[1.75] text-slateink-600" data-id="element-316" data-mp="r74iiocgssk8">
                <p data-id="element-317" data-mp="joy35bwkvcfb">
                  Gerry Holmes is a Dublin-educated, New York-based finance executive who has
                  spent more than two decades inside Tier-1 global institutions, holding
                  senior accountability for financial control, regulatory reporting, and
                  capital governance. He is currently US Head of Finance at Bank of Ireland,
                  where he serves as the principal financial advisor to executive leadership
                  on regional strategy and fiscal governance.
                </p>
                <p data-id="element-318" data-mp="sni9reov5vn6">
                  His career has been defined by the hardest problems in bank finance:
                  interpreting supervisory expectations, rebuilding control environments
                  under examination, and translating dense regulatory frameworks into
                  operating decisions that leadership and directors can act on. That work has
                  taken him across the finance functions of JPMorgan Chase, Deutsche Bank,
                  MUFG, Mizuho, Royal Bank of Scotland, CIT, Barclays, and Merrill Lynch.
                </p>
                <p data-id="element-319" data-mp="r5t9r27g7efx">
                  He is an active Certified Public Accountant licensed in New York State,
                  fluent in both US GAAP and IFRS, and equally comfortable in a technical
                  accounting discussion, a Federal Reserve examination room, or a board
                  committee session. He now seeks independent director and audit committee
                  mandates where that combination of technical depth and executive judgement
                  can strengthen oversight.
                </p>
              </div>

              <div className="mt-14" data-id="element-320" data-mp="1etsxruddkqqt">
                <h3 className="text-[11px] font-semibold uppercase tracking-widest text-navy-700" data-id="element-321" data-mp="3vk14t7b6j3b6">
                  Leadership Philosophy
                </h3>
                <ul className="mt-6 divide-y divide-slateink-300/70 border-y border-slateink-300/70" data-id="element-322" data-mp="3hs1zvi3g21k">
                  {PRINCIPLES.map(principle => <li key={principle.title} className="py-7" data-id="element-323" data-mp="3n3ikfj2dv45c">
                      <p className="font-serif text-lg text-navy-900" data-id="element-324" data-mp="3n53j9kvqd990">{principle.title}</p>
                      <p className="mt-3 text-[15px] leading-relaxed text-slateink-600" data-id="element-325" data-mp="5ssymmrgd05p">
                        {principle.body}
                      </p>
                    </li>)}
                </ul>
              </div>

              <blockquote className="mt-14 border-l-2 border-gold-500 bg-slateink-100 p-8" data-id="element-326" data-mp="34d182p1uyl65">
                <p className="font-serif text-lg leading-relaxed text-navy-900 sm:text-xl" data-id="element-327" data-mp="3j15i74muhodb">
                  “A board's confidence rests on the quality of the numbers in front of it.
                  My work has always been to make those numbers unambiguous.”
                </p>
                <footer className="mt-5 text-[11px] uppercase tracking-widest text-slateink-500" data-id="element-328" data-mp="2qiruhxv83iry">
                  {PROFILE.name}
                </footer>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      <SectionNav currentIndex="01" data-id="element-329" data-mp="3fapb985tpr89" />
    </>;
}